// components/booking/firebase/bookings.js
// Fetch approved bookings from Firestore and provide shared sorting/partitioning
// helpers used by both the list view and calendar view.

import { db } from "../../../utils/firebase";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { ALL_SLOTS } from "./slots";

/**
 * A booking's slots look like ["09:00", "09:30", ...] (matches the format
 * used when reservations are created - see BookingForm). We use the earliest
 * slot on a given date as the booking's sort key / "start time".
 */
export function earliestSlot(slots) {
  if (!slots || slots.length === 0) return "00:00";
  return [...slots].sort()[0];
}

/** Combine a booking's date ("YYYY-MM-DD") + earliest slot ("HH:mm") into a Date object. */
export function bookingStartDate(booking) {
  const time = earliestSlot(booking.slots);
  // booking.date is expected as "YYYY-MM-DD"
  return new Date(`${booking.date}T${time}:00`);
}

/**
 * Subscribe to all approved bookings in Firestore, sorted soonest-first.
 * Returns an unsubscribe function.
 */
// One shared Firestore listener for ALL admin pages (Dashboard, Approved
// Bookings, Manage Bookings). Opening a Firestore listener is the slow part
// (connection + auth handshake + first snapshot), so we open it once, keep it
// alive for the admin session, and hand the latest snapshot to any page that
// subscribes, instantly, instead of every page starting from scratch.
const bookingListeners = new Set();
let unsubscribeFirestore = null;
let latestBookings = null;
let teardownTimer = null;

function ensureBookingsListener() {
  if (unsubscribeFirestore) return;
  const bookingsRef = collection(db, "bookings");
  // Order by date string first (YYYY-MM-DD sorts correctly lexicographically),
  // fine-grained ordering by slot time happens client-side via bookingStartDate.
  const q = query(bookingsRef, orderBy("date", "asc"));

  unsubscribeFirestore = onSnapshot(
    q,
    (snapshot) => {
      const bookings = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      // Secondary sort by earliest slot time within the same date.
      bookings.sort((a, b) => bookingStartDate(a) - bookingStartDate(b));
      latestBookings = bookings;
      bookingListeners.forEach((l) => l.onUpdate(bookings));
    },
    (err) => {
      console.error("[bookings] subscribe error:", err);
      // Reset so the next subscribe() opens a fresh listener.
      unsubscribeFirestore = null;
      latestBookings = null;
      bookingListeners.forEach((l) => l.onError?.(err));
    }
  );
}

/**
 * Subscribe to all approved bookings in Firestore, sorted soonest-first.
 * If a snapshot is already available it is delivered immediately.
 * Returns an unsubscribe function.
 */
export function subscribeToBookings(onUpdate, onError) {
  const listener = { onUpdate, onError };
  bookingListeners.add(listener);
  if (latestBookings) onUpdate(latestBookings);
  ensureBookingsListener();

  clearTimeout(teardownTimer);

  return () => {
    bookingListeners.delete(listener);
    if (bookingListeners.size === 0) {
      // Don't close the Firestore listener right away. React StrictMode (dev)
      // mounts effects twice, and quick page-to-page navigation unsubscribes
      // and re-subscribes within milliseconds; closing and reopening the
      // connection each time is what makes the first load slow.
      teardownTimer = setTimeout(() => {
        if (bookingListeners.size === 0 && unsubscribeFirestore) {
          unsubscribeFirestore();
          unsubscribeFirestore = null;
          latestBookings = null;
        }
      }, 30_000);
    }
  };
}

/**
 * Split a sorted (ascending) bookings list into upcoming vs past relative to now.
 * Upcoming is returned soonest-first; past is returned most-recent-first
 * (i.e. reversed), since "past" should read backward from today toward older dates.
 */
export function partitionBookings(bookings, now = new Date()) {
  const upcoming = [];
  const past = [];

  for (const b of bookings) {
    if (bookingStartDate(b) >= now) {
      upcoming.push(b);
    } else {
      past.push(b);
    }
  }

  past.reverse(); // most recent past booking first
  return { upcoming, past };
}

/**
 * Operating hours for the calendar grid. Adjust if the venue's actual
 * booking window differs - this isn't sourced from anywhere else in the
 * codebase, so it's a matches the client booking page (8 AM–12 AM).
 */
export const CALENDAR_START_HOUR = 8;
export const CALENDAR_END_HOUR = 24;

/** Generate ["06:00", "06:30", "07:00", ...] for the calendar grid. */
export function generateTimeSlots() {
  // Same slot list the client booking page uses (08:00 – 24:00, 30-min slots)
  return ALL_SLOTS.map((s) => s.id);
}

/** Format a Date as "YYYY-MM-DD" to match how booking.date is stored. */
export function toDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Get the Monday of the week containing the given date. */
export function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sunday
  const diff = day === 0 ? -6 : 1 - day; // shift to Monday
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Derive the distinct list of courts present in a bookings array. */
export function deriveCourts(bookings) {
  const set = new Set(bookings.map((b) => b.court).filter(Boolean));
  return Array.from(set).sort();
}

/**
 * Build a lookup: courtName -> dateKey -> slotTime -> booking
 * for fast grid rendering.
 */
export function buildSlotIndex(bookings) {
  const index = {};
  for (const b of bookings) {
    if (!b.court || !b.date) continue;
    index[b.court] = index[b.court] || {};
    index[b.court][b.date] = index[b.court][b.date] || {};
    for (const slot of b.slots || []) {
      index[b.court][b.date][slot] = b;
    }
  }
  return index;
}